import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// PrimeNG v20 imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
// Removed ToastModule - using custom message system instead
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PaginatorModule } from 'primeng/paginator';
import { ToolbarModule } from 'primeng/toolbar';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuModule } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ChartModule } from 'primeng/chart';
import { AccordionModule } from 'primeng/accordion';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';

import { ConfirmationService, MenuItem } from 'primeng/api';
import { CustomMessageService } from '../../core/services/custom-message.service';
import { CustomMessageComponent } from '../../shared/components/custom-message/custom-message.component';
import { GlobalLoadingService } from '../../core/services/global-loading.service';
import { GlobalLoadingDisableDirective } from '../../shared/directives/global-loading-disable.directive';
// Services
import { CustomReportService } from '../../core/services/custom-report.service';
import { StaticReportService } from '../../core/services/static-report.service';
import { AuthService } from '../../core/services/auth';
import { SubscriptionContextService } from '../../core/services/subscription-context.service';
import { StaticReportsComponent } from './static-reports/static-reports.component';
import { STATIC_REPORTS_CONFIG } from './static-reports.config';

// Common settings for filter options
const COMMON_SETTINGS = {
  USER_STATUS: {
    INVITED: "INVITED",
    PENDING: "PENDING", 
    APPROVED: "APPROVED",
    DENIED: "DENIED",
    DEACTIVATED: "DEACTIVATED",
    LOCKED: "LOCKED",
  },
  USER_GENDER: {
    MALE: "MALE",
    FEMALE: "FEMALE", 
    NON_BINARY: "NON_BINARY",
  },
  TRADE_LICENCE: {
    YES: "YES",
    NO: "NO",
  },
  RESERVATION_STATUS: {
    EXPIRED: "Expired",
    PENDING: "Pending",
    INTERESTED: "Interested", 
    NOT_INTERESTED: "Not Interested",
    CONVERTED: "Converted",
  },
  CONTRACT_STATUS: {
    DRAFT: "DRAFT",
    ACTIVE: "ACTIVE",
    EXPIRED: "EXPIRED", 
    REJECTED: "REJECTED",
    TERMINATED: "TERMINATED",
  },
  SUBSCRIBER_STATUS: {
    ACTIVE: "Active",
    INACTIVE: "Inactive",
  }
};

// Interfaces
interface SimpleReport {
  id: string;
  name: string;
  description: string;
  dataSource: string;
  fields: ReportField[];
  filters: ReportFilter[];
  chartConfig?: ChartConfig;
  exportFormats: string[];
  isGlobal: boolean;
  createdAt: Date;
  lastExecuted?: Date;
}

interface ReportField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'array';
  source: string;
  path: string;
  isVisible: boolean;
  isFilterable: boolean;
  isChartable: boolean;
}

interface ReportFilter {
  field: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'select' | 'array';
  operator: string;
  value: any;
  options?: any[]; // For select filters
}

interface ChartConfig {
  enabled: boolean;
  type: 'bar' | 'line' | 'pie' | 'doughnut';
  xAxis: string;
  yAxis: string[];
  title: string;
}

interface ReportData {
  data: any[];
  total: number;
  executionTime: number;
  chartData?: any;
}

@Component({
  selector: 'app-simple-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    InputNumberModule,
    CheckboxModule,
    ToggleSwitchModule,
    CardModule,
    PanelModule,
    DialogModule,
    DrawerModule,
    ConfirmDialogModule,
    CustomMessageComponent,
    ProgressSpinnerModule,
    PaginatorModule,
    ToolbarModule,
    SplitButtonModule,
    MenuModule,
    TagModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    ChartModule,
    AccordionModule,
    TabsModule,
    TextareaModule,
    GlobalLoadingDisableDirective,
    StaticReportsComponent],
  providers: [ConfirmationService],
  templateUrl: './simple-reports.html',
  styleUrl: './simple-reports.scss'
})
export class SimpleReports implements OnInit {
  // Data properties
  reports: SimpleReport[] = [];
  currentReport: SimpleReport | null = null;
  reportData: ReportData | null = null;
  savedReports: any[] = [];
  showSaveDialog = false;
  showLoadDialog = false;
  reportName = '';
  reportDescription = '';
  isPublic = false;
  selectedReportId: string | null = null;
  
  // Data source options
  dataSourceOptions = [
    { label: 'Users', value: 'users', icon: 'pi pi-users' },
    { label: 'Subscribers', value: 'subscribers', icon: 'pi pi-building' },
    { label: 'Customers', value: 'customers', icon: 'pi pi-briefcase' },
    { label: 'Products', value: 'products', icon: 'pi pi-box' },
    { label: 'Reservations', value: 'reservations', icon: 'pi pi-calendar' },
    { label: 'Contracts', value: 'contracts', icon: 'pi pi-file' },
    { label: 'Interactions', value: 'interactions', icon: 'pi pi-comments' },
    { label: 'Territories', value: 'territories', icon: 'pi pi-map' }
  ];
  
  // UI state
  loading = false;
  reportBuilderVisible = false;
  reportExecutionVisible = false;
  chartViewVisible = false;
  
  // Forms
  reportForm!: FormGroup;
  filterForm!: FormGroup;
  
  // Report builder
  selectedDataSource = '';
  selectedFields: ReportField[] = [];
  selectedFilters: ReportFilter[] = [];
  chartConfig: ChartConfig | null = null;
  
  // Pagination
  currentPage = 1;
  pageSize = 25;  // Increased from 10 to 25 to match backend default and reduce requests
  totalRecords = 0;
  
  // Chart data
  chartData: any = null;
  chartOptions: any = {};
  chartError: string | null = null; // Track chart generation errors
  
  // Available data sources and fields
  dataSources = [
    { label: 'Users', value: 'users', icon: 'pi pi-users' },
    { label: 'Subscribers', value: 'subscribers', icon: 'pi pi-building' },
    { label: 'Customers', value: 'customers', icon: 'pi pi-user' },
    { label: 'Products', value: 'products', icon: 'pi pi-box' },
    { label: 'Reservations', value: 'reservations', icon: 'pi pi-calendar' },
    { label: 'Contracts', value: 'contracts', icon: 'pi pi-file' },
    { label: 'Interactions', value: 'interactions', icon: 'pi pi-comments' }
  ];

  // Model relationships mapping
  modelRelationships: { [key: string]: { [key: string]: string } } = {
    'reservations': {
      'userId': 'users',
      'subscriberId': 'subscribers', 
      'customerId': 'customers',
      'products': 'products'
    },
    'contracts': {
      'userId': 'users',
      'subscriberId': 'subscribers',
      'customerId': 'customers',
      'products': 'products',
      'fsrId': 'users',
      'approverId': 'users',
      'reservationId': 'reservations'
    },
    'interactions': {
      'subscriberId': 'subscribers',
      'reservationId': 'reservations'
    },
    'users': {
      'subscriberId': 'subscribers',
      'roleId': 'roles',
      'territories': 'territories',
      'products': 'products'
    },
    'customers': {
      'subscriberId': 'subscribers'
    },
    'products': {
      'subscriberId': 'subscribers',
      'availableTerritories': 'territories'
    },
    'territories': {
      'subscriberId': 'subscribers'
    },
    'subscribers': {
      'userId': 'users'
    }
  };
  
  // Field definitions for each data source
  fieldDefinitions: { [key: string]: ReportField[] } = {
    ['users']: [
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'users', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'title', label: 'Title', type: 'string', source: 'users', path: 'title', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'firstName', label: 'First Name', type: 'string', source: 'users', path: 'firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'lastName', label: 'Last Name', type: 'string', source: 'users', path: 'lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'gender', label: 'Gender', type: 'string', source: 'users', path: 'gender', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'email', label: 'Email', type: 'string', source: 'users', path: 'email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'mobileNumber', label: 'Mobile Number', type: 'string', source: 'users', path: 'mobileNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'companyName', label: 'Company Name', type: 'string', source: 'users', path: 'companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'citizenship', label: 'Citizenship', type: 'string', source: 'users', path: 'citizenship', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'nationalInsuranceNo', label: 'National Insurance No', type: 'string', source: 'users', path: 'nationalInsuranceNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'taxNumber', label: 'Tax Number', type: 'string', source: 'users', path: 'taxNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'tradeLicence', label: 'Trade Licence', type: 'string', source: 'users', path: 'tradeLicence', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'idCard', label: 'ID Card', type: 'string', source: 'users', path: 'idCard', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'isActive', label: 'Active', type: 'boolean', source: 'users', path: 'isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'isArchived', label: 'Archived', type: 'boolean', source: 'users', path: 'isArchived', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'status', label: 'Status', type: 'string', source: 'users', path: 'status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'loginAttempts', label: 'Login Attempts', type: 'number', source: 'users', path: 'loginAttempts', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'isFirstLogin', label: 'First Login', type: 'boolean', source: 'users', path: 'isFirstLogin', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'accountSetupStep', label: 'Account Setup Step', type: 'number', source: 'users', path: 'accountSetupStep', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'address.streetAddress', label: 'Street Address', type: 'string', source: 'users', path: 'address.streetAddress', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'address.houseNumber', label: 'House Number', type: 'string', source: 'users', path: 'address.houseNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'address.postcode', label: 'Postcode', type: 'string', source: 'users', path: 'address.postcode', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'address.city', label: 'City', type: 'string', source: 'users', path: 'address.city', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'address.country', label: 'Country', type: 'string', source: 'users', path: 'address.country', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'users', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'users', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Subscriber fields
      { name: 'subscriber.refNo', label: 'Subscriber Reference', type: 'string', source: 'users', path: 'subscriberId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.companyName', label: 'Subscriber Company', type: 'string', source: 'users', path: 'subscriberId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.firstName', label: 'Subscriber First Name', type: 'string', source: 'users', path: 'subscriberId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.lastName', label: 'Subscriber Last Name', type: 'string', source: 'users', path: 'subscriberId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.email', label: 'Subscriber Email', type: 'string', source: 'users', path: 'subscriberId.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.status', label: 'Subscriber Status', type: 'string', source: 'users', path: 'subscriberId.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.isPaid', label: 'Subscriber Paid', type: 'boolean', source: 'users', path: 'subscriberId.isPaid', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.language', label: 'Subscriber Language', type: 'string', source: 'users', path: 'subscriberId.language', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.contactDuration', label: 'Subscriber Contact Duration', type: 'string', source: 'users', path: 'subscriberId.contactDuration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.industries', label: 'Subscriber Industries', type: 'array', source: 'users', path: 'subscriberId.industries', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.operationalRegions', label: 'Subscriber Operational Regions', type: 'array', source: 'users', path: 'subscriberId.operationalRegions', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.payment.status', label: 'Subscriber Payment Status', type: 'string', source: 'users', path: 'subscriberId.payment.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.payment.planName', label: 'Subscriber Payment Plan', type: 'string', source: 'users', path: 'subscriberId.payment.planName', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'subscriber.createdAt', label: 'Subscriber Created Date', type: 'date', source: 'users', path: 'subscriberId.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Role fields
      { name: 'role.name', label: 'Role Name', type: 'string', source: 'users', path: 'roleId.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'role.identification', label: 'Role Identification', type: 'string', source: 'users', path: 'roleId.identification', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'role.description', label: 'Role Description', type: 'string', source: 'users', path: 'roleId.description', isVisible: true, isFilterable: true, isChartable: false },
      
      // Related Territories fields
      { name: 'territories.refNo', label: 'Territories Reference', type: 'array', source: 'users', path: 'territories.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'territories.name', label: 'Territories Name', type: 'array', source: 'users', path: 'territories.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'territories.region', label: 'Territories Region', type: 'array', source: 'users', path: 'territories.region', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'territories.status', label: 'Territories Status', type: 'array', source: 'users', path: 'territories.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Products fields
      { name: 'products.refNo', label: 'Products Reference', type: 'array', source: 'users', path: 'products.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.name', label: 'Products Name', type: 'array', source: 'users', path: 'products.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.code', label: 'Products Code', type: 'array', source: 'users', path: 'products.code', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.description', label: 'Products Description', type: 'array', source: 'users', path: 'products.description', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.minPrice', label: 'Products Min Price', type: 'array', source: 'users', path: 'products.minPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.maxPrice', label: 'Products Max Price', type: 'array', source: 'users', path: 'products.maxPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.price', label: 'Products Price', type: 'array', source: 'users', path: 'products.price', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.currency', label: 'Products Currency', type: 'array', source: 'users', path: 'products.currency', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isActive', label: 'Products Active', type: 'array', source: 'users', path: 'products.isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isArchived', label: 'Products Archived', type: 'array', source: 'users', path: 'products.isArchived', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Reservations fields
      { name: 'reservations.refNo', label: 'Reservations Reference', type: 'array', source: 'users', path: 'reservations.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'reservations.status', label: 'Reservations Status', type: 'array', source: 'users', path: 'reservations.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.expiryDate', label: 'Reservations Expiry Date', type: 'array', source: 'users', path: 'reservations.expiryDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.reminderSentAt', label: 'Reservations Reminder Sent At', type: 'array', source: 'users', path: 'reservations.reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.createdAt', label: 'Reservations Created Date', type: 'array', source: 'users', path: 'reservations.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.updatedAt', label: 'Reservations Updated Date', type: 'array', source: 'users', path: 'reservations.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Contracts fields
      { name: 'contracts.refNo', label: 'Contracts Reference', type: 'array', source: 'users', path: 'contracts.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'contracts.status', label: 'Contracts Status', type: 'array', source: 'users', path: 'contracts.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.contractValue', label: 'Contracts Value', type: 'array', source: 'users', path: 'contracts.contractValue', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.duration', label: 'Contracts Duration', type: 'array', source: 'users', path: 'contracts.duration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.startDate', label: 'Contracts Start Date', type: 'array', source: 'users', path: 'contracts.startDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.endDate', label: 'Contracts End Date', type: 'array', source: 'users', path: 'contracts.endDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.createdAt', label: 'Contracts Created Date', type: 'array', source: 'users', path: 'contracts.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.updatedAt', label: 'Contracts Updated Date', type: 'array', source: 'users', path: 'contracts.updatedAt', isVisible: true, isFilterable: true, isChartable: true }
    ],
    ['subscribers']: [
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'subscribers', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'companyName', label: 'Company Name', type: 'string', source: 'subscribers', path: 'companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'firstName', label: 'First Name', type: 'string', source: 'subscribers', path: 'firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'lastName', label: 'Last Name', type: 'string', source: 'subscribers', path: 'lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'email', label: 'Email', type: 'string', source: 'subscribers', path: 'email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'status', label: 'Status', type: 'string', source: 'subscribers', path: 'status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'isPaid', label: 'Paid', type: 'boolean', source: 'subscribers', path: 'isPaid', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'companyLogo', label: 'Company Logo', type: 'string', source: 'subscribers', path: 'companyLogo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'termsConditions', label: 'Terms & Conditions', type: 'string', source: 'subscribers', path: 'termsConditions', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'privacyPolicy', label: 'Privacy Policy', type: 'string', source: 'subscribers', path: 'privacyPolicy', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'industries', label: 'Industries', type: 'array', source: 'subscribers', path: 'industries', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contractDurationsList', label: 'Contract Durations', type: 'array', source: 'subscribers', path: 'contractDurationsList', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservationDurationsList', label: 'Reservation Durations', type: 'array', source: 'subscribers', path: 'reservationDurationsList', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'operationalRegions', label: 'Operational Regions', type: 'array', source: 'subscribers', path: 'operationalRegions', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contactDuration', label: 'Contact Duration', type: 'string', source: 'subscribers', path: 'contactDuration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'language', label: 'Language', type: 'string', source: 'subscribers', path: 'language', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related User fields (userId - the owner/creator)
      { name: 'user.refNo', label: 'User Reference', type: 'string', source: 'subscribers', path: 'userId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.firstName', label: 'User First Name', type: 'string', source: 'subscribers', path: 'userId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.lastName', label: 'User Last Name', type: 'string', source: 'subscribers', path: 'userId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.email', label: 'User Email', type: 'string', source: 'subscribers', path: 'userId.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.status', label: 'User Status', type: 'string', source: 'subscribers', path: 'userId.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'payment.registeredEmail', label: 'Payment Email', type: 'string', source: 'subscribers', path: 'payment.registeredEmail', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'payment.customerId', label: 'Payment Customer ID', type: 'string', source: 'subscribers', path: 'payment.customerId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'payment.status', label: 'Payment Status', type: 'string', source: 'subscribers', path: 'payment.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'payment.planId', label: 'Payment Plan ID', type: 'string', source: 'subscribers', path: 'payment.planId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'payment.planName', label: 'Payment Plan Name', type: 'string', source: 'subscribers', path: 'payment.planName', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'subscribers', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'subscribers', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Users fields (all users belonging to this subscriber)
      { name: 'users.refNo', label: 'Users Reference', type: 'array', source: 'subscribers', path: 'users.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.firstName', label: 'Users First Name', type: 'array', source: 'subscribers', path: 'users.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.lastName', label: 'Users Last Name', type: 'array', source: 'subscribers', path: 'users.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.email', label: 'Users Email', type: 'array', source: 'subscribers', path: 'users.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.mobileNumber', label: 'Users Mobile', type: 'array', source: 'subscribers', path: 'users.mobileNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.status', label: 'Users Status', type: 'array', source: 'subscribers', path: 'users.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'users.isActive', label: 'Users Active', type: 'array', source: 'subscribers', path: 'users.isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'users.companyName', label: 'Users Company', type: 'array', source: 'subscribers', path: 'users.companyName', isVisible: true, isFilterable: true, isChartable: false },
      
      // Related Territories fields
      { name: 'territories.refNo', label: 'Territories Reference', type: 'array', source: 'subscribers', path: 'territories.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'territories.name', label: 'Territories Name', type: 'array', source: 'subscribers', path: 'territories.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'territories.region', label: 'Territories Region', type: 'array', source: 'subscribers', path: 'territories.region', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'territories.status', label: 'Territories Status', type: 'array', source: 'subscribers', path: 'territories.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Products fields
      { name: 'products.refNo', label: 'Products Reference', type: 'array', source: 'subscribers', path: 'products.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.name', label: 'Products Name', type: 'array', source: 'subscribers', path: 'products.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.code', label: 'Products Code', type: 'array', source: 'subscribers', path: 'products.code', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.description', label: 'Products Description', type: 'array', source: 'subscribers', path: 'products.description', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.minPrice', label: 'Products Min Price', type: 'array', source: 'subscribers', path: 'products.minPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.maxPrice', label: 'Products Max Price', type: 'array', source: 'subscribers', path: 'products.maxPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.price', label: 'Products Price', type: 'array', source: 'subscribers', path: 'products.price', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.currency', label: 'Products Currency', type: 'array', source: 'subscribers', path: 'products.currency', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isActive', label: 'Products Active', type: 'array', source: 'subscribers', path: 'products.isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isArchived', label: 'Products Archived', type: 'array', source: 'subscribers', path: 'products.isArchived', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Customers fields
      { name: 'customers.refNo', label: 'Customers Reference', type: 'array', source: 'subscribers', path: 'customers.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customers.businessName', label: 'Customers Business Name', type: 'array', source: 'subscribers', path: 'customers.businessName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customers.businessId', label: 'Customers Business ID', type: 'array', source: 'subscribers', path: 'customers.businessId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customers.industry', label: 'Customers Industry', type: 'array', source: 'subscribers', path: 'customers.industry', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customers.numberOfEmployees', label: 'Customers Number of Employees', type: 'array', source: 'subscribers', path: 'customers.numberOfEmployees', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customers.interestLevel', label: 'Customers Interest Level', type: 'array', source: 'subscribers', path: 'customers.interestLevel', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customers.acceptedPromotionalGifts', label: 'Customers Accepted Promotional Gifts', type: 'array', source: 'subscribers', path: 'customers.acceptedPromotionalGifts', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customers.address.city', label: 'Customers City', type: 'array', source: 'subscribers', path: 'customers.address.city', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customers.address.country', label: 'Customers Country', type: 'array', source: 'subscribers', path: 'customers.address.country', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Reservations fields
      { name: 'reservations.refNo', label: 'Reservations Reference', type: 'array', source: 'subscribers', path: 'reservations.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'reservations.status', label: 'Reservations Status', type: 'array', source: 'subscribers', path: 'reservations.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.expiryDate', label: 'Reservations Expiry Date', type: 'array', source: 'subscribers', path: 'reservations.expiryDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.reminderSentAt', label: 'Reservations Reminder Sent At', type: 'array', source: 'subscribers', path: 'reservations.reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.createdAt', label: 'Reservations Created Date', type: 'array', source: 'subscribers', path: 'reservations.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.updatedAt', label: 'Reservations Updated Date', type: 'array', source: 'subscribers', path: 'reservations.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Contracts fields
      { name: 'contracts.refNo', label: 'Contracts Reference', type: 'array', source: 'subscribers', path: 'contracts.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'contracts.status', label: 'Contracts Status', type: 'array', source: 'subscribers', path: 'contracts.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.contractValue', label: 'Contracts Value', type: 'array', source: 'subscribers', path: 'contracts.contractValue', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.duration', label: 'Contracts Duration', type: 'array', source: 'subscribers', path: 'contracts.duration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.startDate', label: 'Contracts Start Date', type: 'array', source: 'subscribers', path: 'contracts.startDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.endDate', label: 'Contracts End Date', type: 'array', source: 'subscribers', path: 'contracts.endDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.createdAt', label: 'Contracts Created Date', type: 'array', source: 'subscribers', path: 'contracts.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Interactions fields
      { name: 'interactions.refNo', label: 'Interactions Reference', type: 'array', source: 'subscribers', path: 'interactions.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'interactions.interactionType', label: 'Interactions Type', type: 'array', source: 'subscribers', path: 'interactions.interactionType', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.interactionStatus', label: 'Interactions Status', type: 'array', source: 'subscribers', path: 'interactions.interactionStatus', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.interactionDate', label: 'Interactions Date', type: 'array', source: 'subscribers', path: 'interactions.interactionDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.createdAt', label: 'Interactions Created Date', type: 'array', source: 'subscribers', path: 'interactions.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.updatedAt', label: 'Interactions Updated Date', type: 'array', source: 'subscribers', path: 'interactions.updatedAt', isVisible: true, isFilterable: true, isChartable: true }
    ],
    ['customers']: [
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'customers', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'businessName', label: 'Business Name', type: 'string', source: 'customers', path: 'businessName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'businessId', label: 'Business ID', type: 'string', source: 'customers', path: 'businessId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'zipCode', label: 'Zip Code', type: 'string', source: 'customers', path: 'zipCode', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'industry', label: 'Industry', type: 'string', source: 'customers', path: 'industry', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'numberOfEmployees', label: 'Number of Employees', type: 'string', source: 'customers', path: 'numberOfEmployees', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interestLevel', label: 'Interest Level', type: 'string', source: 'customers', path: 'interestLevel', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'acceptedPromotionalGifts', label: 'Accepted Promotional Gifts', type: 'boolean', source: 'customers', path: 'acceptedPromotionalGifts', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'documents', label: 'Documents', type: 'string', source: 'customers', path: 'documents', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customerOrigin', label: 'Customer Origin', type: 'string', source: 'customers', path: 'customerOrigin', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'notes', label: 'Notes', type: 'string', source: 'customers', path: 'notes', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'googlePlaceId', label: 'Google Place ID', type: 'string', source: 'customers', path: 'googlePlaceId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'decisionMaker', label: 'Decision Makers', type: 'array', source: 'customers', path: 'decisionMaker', isVisible: true, isFilterable: false, isChartable: false },
      { name: 'address.streetAddress', label: 'Street Address', type: 'string', source: 'customers', path: 'address.streetAddress', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'address.city', label: 'City', type: 'string', source: 'customers', path: 'address.city', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'address.state', label: 'State', type: 'string', source: 'customers', path: 'address.state', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'address.country', label: 'Country', type: 'string', source: 'customers', path: 'address.country', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'address.formattedAddress', label: 'Formatted Address', type: 'string', source: 'customers', path: 'address.formattedAddress', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'customers', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'customers', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Subscriber fields
      { name: 'subscriber.refNo', label: 'Subscriber Reference', type: 'string', source: 'customers', path: 'subscriberId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.companyName', label: 'Subscriber Company', type: 'string', source: 'customers', path: 'subscriberId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.status', label: 'Subscriber Status', type: 'string', source: 'customers', path: 'subscriberId.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Reservations fields
      { name: 'reservations.refNo', label: 'Reservations Reference', type: 'array', source: 'customers', path: 'reservations.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'reservations.status', label: 'Reservations Status', type: 'array', source: 'customers', path: 'reservations.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.expiryDate', label: 'Reservations Expiry Date', type: 'array', source: 'customers', path: 'reservations.expiryDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.reminderSentAt', label: 'Reservations Reminder Sent At', type: 'array', source: 'customers', path: 'reservations.reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.createdAt', label: 'Reservations Created Date', type: 'array', source: 'customers', path: 'reservations.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.updatedAt', label: 'Reservations Updated Date', type: 'array', source: 'customers', path: 'reservations.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Contracts fields
      { name: 'contracts.refNo', label: 'Contracts Reference', type: 'array', source: 'customers', path: 'contracts.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'contracts.status', label: 'Contracts Status', type: 'array', source: 'customers', path: 'contracts.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.contractValue', label: 'Contracts Value', type: 'array', source: 'customers', path: 'contracts.contractValue', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.startDate', label: 'Contracts Start Date', type: 'array', source: 'customers', path: 'contracts.startDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.endDate', label: 'Contracts End Date', type: 'array', source: 'customers', path: 'contracts.endDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.createdAt', label: 'Contracts Created Date', type: 'array', source: 'customers', path: 'contracts.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Interactions fields (through reservations)
      { name: 'interactions.refNo', label: 'Interactions Reference', type: 'array', source: 'customers', path: 'interactions.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'interactions.interactionType', label: 'Interactions Type', type: 'array', source: 'customers', path: 'interactions.interactionType', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.interactionStatus', label: 'Interactions Status', type: 'array', source: 'customers', path: 'interactions.interactionStatus', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.interactionDate', label: 'Interactions Date', type: 'array', source: 'customers', path: 'interactions.interactionDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.createdAt', label: 'Interactions Created Date', type: 'array', source: 'customers', path: 'interactions.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.updatedAt', label: 'Interactions Updated Date', type: 'array', source: 'customers', path: 'interactions.updatedAt', isVisible: true, isFilterable: true, isChartable: true }
    ],
    ['products']: [
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'products', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'name', label: 'Product Name', type: 'string', source: 'products', path: 'name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'code', label: 'Product Code', type: 'string', source: 'products', path: 'code', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'description', label: 'Description', type: 'string', source: 'products', path: 'description', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'minPrice', label: 'Min Price', type: 'number', source: 'products', path: 'minPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'maxPrice', label: 'Max Price', type: 'number', source: 'products', path: 'maxPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'price', label: 'Price', type: 'string', source: 'products', path: 'price', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'currency', label: 'Currency', type: 'string', source: 'products', path: 'currency', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations', label: 'Reservations Count', type: 'number', source: 'products', path: 'reservations', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'isActive', label: 'Active', type: 'boolean', source: 'products', path: 'isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'isArchived', label: 'Archived', type: 'boolean', source: 'products', path: 'isArchived', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'products', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'products', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Subscriber fields
      { name: 'subscriber.refNo', label: 'Subscriber Reference', type: 'string', source: 'products', path: 'subscriberId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.companyName', label: 'Subscriber Company', type: 'string', source: 'products', path: 'subscriberId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.status', label: 'Subscriber Status', type: 'string', source: 'products', path: 'subscriberId.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Territories fields
      { name: 'territories.refNo', label: 'Territories Reference', type: 'array', source: 'products', path: 'availableTerritories.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'territories.name', label: 'Territories Name', type: 'array', source: 'products', path: 'availableTerritories.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'territories.region', label: 'Territories Region', type: 'array', source: 'products', path: 'availableTerritories.region', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'territories.status', label: 'Territories Status', type: 'array', source: 'products', path: 'availableTerritories.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Users fields (users who have access to this product)
      { name: 'users.refNo', label: 'Users Reference', type: 'array', source: 'products', path: 'users.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.firstName', label: 'Users First Name', type: 'array', source: 'products', path: 'users.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.lastName', label: 'Users Last Name', type: 'array', source: 'products', path: 'users.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.email', label: 'Users Email', type: 'array', source: 'products', path: 'users.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.status', label: 'Users Status', type: 'array', source: 'products', path: 'users.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Reservations fields
      { name: 'reservations.refNo', label: 'Reservations Reference', type: 'array', source: 'products', path: 'reservations.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'reservations.status', label: 'Reservations Status', type: 'array', source: 'products', path: 'reservations.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.expiryDate', label: 'Reservations Expiry Date', type: 'array', source: 'products', path: 'reservations.expiryDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.createdAt', label: 'Reservations Created Date', type: 'array', source: 'products', path: 'reservations.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservations.updatedAt', label: 'Reservations Updated Date', type: 'array', source: 'products', path: 'reservations.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Contracts fields
      { name: 'contracts.refNo', label: 'Contracts Reference', type: 'array', source: 'products', path: 'contracts.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'contracts.status', label: 'Contracts Status', type: 'array', source: 'products', path: 'contracts.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.contractValue', label: 'Contracts Value', type: 'array', source: 'products', path: 'contracts.contractValue', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.duration', label: 'Contracts Duration', type: 'array', source: 'products', path: 'contracts.duration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.startDate', label: 'Contracts Start Date', type: 'array', source: 'products', path: 'contracts.startDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.endDate', label: 'Contracts End Date', type: 'array', source: 'products', path: 'contracts.endDate', isVisible: true, isFilterable: true, isChartable: true }
    ],
    ['reservations']: [
      // Reservation fields
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'reservations', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'status', label: 'Status', type: 'string', source: 'reservations', path: 'status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'expiryDate', label: 'Expiry Date', type: 'date', source: 'reservations', path: 'expiryDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reminderSentAt', label: 'Reminder Sent At', type: 'date', source: 'reservations', path: 'reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'reservations', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'reservations', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Subscriber fields
      { name: 'subscriber.refNo', label: 'Subscriber Reference', type: 'string', source: 'reservations', path: 'subscriberId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.companyName', label: 'Subscriber Company', type: 'string', source: 'reservations', path: 'subscriberId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.status', label: 'Subscriber Status', type: 'string', source: 'reservations', path: 'subscriberId.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related User fields
      { name: 'user.refNo', label: 'User Reference', type: 'string', source: 'reservations', path: 'userId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.title', label: 'User Title', type: 'string', source: 'reservations', path: 'userId.title', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.firstName', label: 'User First Name', type: 'string', source: 'reservations', path: 'userId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.lastName', label: 'User Last Name', type: 'string', source: 'reservations', path: 'userId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.gender', label: 'User Gender', type: 'string', source: 'reservations', path: 'userId.gender', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.email', label: 'User Email', type: 'string', source: 'reservations', path: 'userId.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.mobileNumber', label: 'User Mobile', type: 'string', source: 'reservations', path: 'userId.mobileNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.companyName', label: 'User Company', type: 'string', source: 'reservations', path: 'userId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.citizenship', label: 'User Citizenship', type: 'string', source: 'reservations', path: 'userId.citizenship', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.nationalInsuranceNo', label: 'User National Insurance', type: 'string', source: 'reservations', path: 'userId.nationalInsuranceNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.taxNumber', label: 'User Tax Number', type: 'string', source: 'reservations', path: 'userId.taxNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.tradeLicence', label: 'User Trade Licence', type: 'string', source: 'reservations', path: 'userId.tradeLicence', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.idCard', label: 'User ID Card', type: 'string', source: 'reservations', path: 'userId.idCard', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.isActive', label: 'User Active', type: 'boolean', source: 'reservations', path: 'userId.isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.isArchived', label: 'User Archived', type: 'boolean', source: 'reservations', path: 'userId.isArchived', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.status', label: 'User Status', type: 'string', source: 'reservations', path: 'userId.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.loginAttempts', label: 'User Login Attempts', type: 'number', source: 'reservations', path: 'userId.loginAttempts', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.isFirstLogin', label: 'User First Login', type: 'boolean', source: 'reservations', path: 'userId.isFirstLogin', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.accountSetupStep', label: 'User Account Setup Step', type: 'number', source: 'reservations', path: 'userId.accountSetupStep', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.address.streetAddress', label: 'User Street Address', type: 'string', source: 'reservations', path: 'userId.address.streetAddress', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.address.houseNumber', label: 'User House Number', type: 'string', source: 'reservations', path: 'userId.address.houseNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.address.postcode', label: 'User Postcode', type: 'string', source: 'reservations', path: 'userId.address.postcode', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.address.city', label: 'User City', type: 'string', source: 'reservations', path: 'userId.address.city', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.address.country', label: 'User Country', type: 'string', source: 'reservations', path: 'userId.address.country', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.createdAt', label: 'User Created Date', type: 'date', source: 'reservations', path: 'userId.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.updatedAt', label: 'User Updated Date', type: 'date', source: 'reservations', path: 'userId.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Customer fields
      { name: 'customer.refNo', label: 'Customer Reference', type: 'string', source: 'reservations', path: 'customerId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.businessName', label: 'Business Name', type: 'string', source: 'reservations', path: 'customerId.businessName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.businessId', label: 'Business ID', type: 'string', source: 'reservations', path: 'customerId.businessId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.zipCode', label: 'Customer Zip Code', type: 'string', source: 'reservations', path: 'customerId.zipCode', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.industry', label: 'Industry', type: 'string', source: 'reservations', path: 'customerId.industry', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.numberOfEmployees', label: 'Number of Employees', type: 'string', source: 'reservations', path: 'customerId.numberOfEmployees', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.interestLevel', label: 'Interest Level', type: 'string', source: 'reservations', path: 'customerId.interestLevel', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.acceptedPromotionalGifts', label: 'Accepted Promotional Gifts', type: 'boolean', source: 'reservations', path: 'customerId.acceptedPromotionalGifts', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.documents', label: 'Customer Documents', type: 'string', source: 'reservations', path: 'customerId.documents', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.googlePlaceId', label: 'Google Place ID', type: 'string', source: 'reservations', path: 'customerId.googlePlaceId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.address.streetAddress', label: 'Customer Street Address', type: 'string', source: 'reservations', path: 'customerId.address.streetAddress', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.address.city', label: 'Customer City', type: 'string', source: 'reservations', path: 'customerId.address.city', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.address.state', label: 'Customer State', type: 'string', source: 'reservations', path: 'customerId.address.state', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.address.country', label: 'Customer Country', type: 'string', source: 'reservations', path: 'customerId.address.country', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.address.formattedAddress', label: 'Customer Formatted Address', type: 'string', source: 'reservations', path: 'customerId.address.formattedAddress', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.createdAt', label: 'Customer Created Date', type: 'date', source: 'reservations', path: 'customerId.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.updatedAt', label: 'Customer Updated Date', type: 'date', source: 'reservations', path: 'customerId.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Product fields (for products array)
      { name: 'products.refNo', label: 'Product References', type: 'array', source: 'reservations', path: 'products.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.name', label: 'Product Names', type: 'array', source: 'reservations', path: 'products.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.code', label: 'Product Codes', type: 'array', source: 'reservations', path: 'products.code', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.description', label: 'Product Descriptions', type: 'array', source: 'reservations', path: 'products.description', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.minPrice', label: 'Product Min Prices', type: 'array', source: 'reservations', path: 'products.minPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.maxPrice', label: 'Product Max Prices', type: 'array', source: 'reservations', path: 'products.maxPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.price', label: 'Product Prices', type: 'array', source: 'reservations', path: 'products.price', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.currency', label: 'Product Currencies', type: 'array', source: 'reservations', path: 'products.currency', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.reservations', label: 'Product Reservations Count', type: 'array', source: 'reservations', path: 'products.reservations', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isActive', label: 'Product Active Status', type: 'array', source: 'reservations', path: 'products.isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isArchived', label: 'Product Archived Status', type: 'array', source: 'reservations', path: 'products.isArchived', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.createdAt', label: 'Product Created Dates', type: 'array', source: 'reservations', path: 'products.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.updatedAt', label: 'Product Updated Dates', type: 'array', source: 'reservations', path: 'products.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Interactions fields
      { name: 'interactions.refNo', label: 'Interactions Reference', type: 'array', source: 'reservations', path: 'interactions.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'interactions.interactionType', label: 'Interactions Type', type: 'array', source: 'reservations', path: 'interactions.interactionType', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.interactionStatus', label: 'Interactions Status', type: 'array', source: 'reservations', path: 'interactions.interactionStatus', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.interactionDate', label: 'Interactions Date', type: 'array', source: 'reservations', path: 'interactions.interactionDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.interactionTime', label: 'Interactions Time', type: 'array', source: 'reservations', path: 'interactions.interactionTime', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'interactions.reservationStatus', label: 'Interactions Reservation Status', type: 'array', source: 'reservations', path: 'interactions.reservationStatus', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.reminderSentAt', label: 'Interactions Reminder Sent At', type: 'array', source: 'reservations', path: 'interactions.reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.createdAt', label: 'Interactions Created Date', type: 'array', source: 'reservations', path: 'interactions.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactions.updatedAt', label: 'Interactions Updated Date', type: 'array', source: 'reservations', path: 'interactions.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Contracts fields
      { name: 'contracts.refNo', label: 'Contracts Reference', type: 'array', source: 'reservations', path: 'contracts.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'contracts.status', label: 'Contracts Status', type: 'array', source: 'reservations', path: 'contracts.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.contractValue', label: 'Contracts Value', type: 'array', source: 'reservations', path: 'contracts.contractValue', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.duration', label: 'Contracts Duration', type: 'array', source: 'reservations', path: 'contracts.duration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.startDate', label: 'Contracts Start Date', type: 'array', source: 'reservations', path: 'contracts.startDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.endDate', label: 'Contracts End Date', type: 'array', source: 'reservations', path: 'contracts.endDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.createdAt', label: 'Contracts Created Date', type: 'array', source: 'reservations', path: 'contracts.createdAt', isVisible: true, isFilterable: true, isChartable: true }
    ],
    ['contracts']: [
      // Contract fields
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'contracts', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products', label: 'Products', type: 'array', source: 'contracts', path: 'products', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contractValue', label: 'Contract Value', type: 'number', source: 'contracts', path: 'contractValue', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'duration', label: 'Duration', type: 'string', source: 'contracts', path: 'duration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'startDate', label: 'Start Date', type: 'date', source: 'contracts', path: 'startDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'endDate', label: 'End Date', type: 'date', source: 'contracts', path: 'endDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'description', label: 'Description', type: 'string', source: 'contracts', path: 'description', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'status', label: 'Status', type: 'string', source: 'contracts', path: 'status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'documents', label: 'Documents', type: 'array', source: 'contracts', path: 'documents', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'contracts', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'contracts', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Subscriber fields
      { name: 'subscriber.refNo', label: 'Subscriber Reference', type: 'string', source: 'contracts', path: 'subscriberId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.companyName', label: 'Subscriber Company', type: 'string', source: 'contracts', path: 'subscriberId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.status', label: 'Subscriber Status', type: 'string', source: 'contracts', path: 'subscriberId.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Reservation fields
      { name: 'reservation.refNo', label: 'Reservation Reference', type: 'string', source: 'contracts', path: 'reservationId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'reservation.status', label: 'Reservation Status', type: 'string', source: 'contracts', path: 'reservationId.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.expiryDate', label: 'Reservation Expiry Date', type: 'date', source: 'contracts', path: 'reservationId.expiryDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.reminderSentAt', label: 'Reservation Reminder Sent At', type: 'date', source: 'contracts', path: 'reservationId.reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.createdAt', label: 'Reservation Created Date', type: 'date', source: 'contracts', path: 'reservationId.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.updatedAt', label: 'Reservation Updated Date', type: 'date', source: 'contracts', path: 'reservationId.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related FSR User fields (fsrId)
      { name: 'fsr.refNo', label: 'FSR Reference', type: 'string', source: 'contracts', path: 'fsrId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'fsr.firstName', label: 'FSR First Name', type: 'string', source: 'contracts', path: 'fsrId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'fsr.lastName', label: 'FSR Last Name', type: 'string', source: 'contracts', path: 'fsrId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'fsr.email', label: 'FSR Email', type: 'string', source: 'contracts', path: 'fsrId.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'fsr.mobileNumber', label: 'FSR Mobile', type: 'string', source: 'contracts', path: 'fsrId.mobileNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'fsr.status', label: 'FSR Status', type: 'string', source: 'contracts', path: 'fsrId.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Approver User fields (approverId - optional)
      { name: 'approver.refNo', label: 'Approver Reference', type: 'string', source: 'contracts', path: 'approverId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'approver.firstName', label: 'Approver First Name', type: 'string', source: 'contracts', path: 'approverId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'approver.lastName', label: 'Approver Last Name', type: 'string', source: 'contracts', path: 'approverId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'approver.email', label: 'Approver Email', type: 'string', source: 'contracts', path: 'approverId.email', isVisible: true, isFilterable: true, isChartable: false },
      
      // Related Customer fields
      { name: 'customer.refNo', label: 'Customer Reference', type: 'string', source: 'contracts', path: 'customerId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.businessName', label: 'Business Name', type: 'string', source: 'contracts', path: 'customerId.businessName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.businessId', label: 'Business ID', type: 'string', source: 'contracts', path: 'customerId.businessId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.industry', label: 'Industry', type: 'string', source: 'contracts', path: 'customerId.industry', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.numberOfEmployees', label: 'Number of Employees', type: 'string', source: 'contracts', path: 'customerId.numberOfEmployees', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.interestLevel', label: 'Interest Level', type: 'string', source: 'contracts', path: 'customerId.interestLevel', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.address.city', label: 'Customer City', type: 'string', source: 'contracts', path: 'customerId.address.city', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.address.country', label: 'Customer Country', type: 'string', source: 'contracts', path: 'customerId.address.country', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Product fields (products array contains product refNos/IDs)
      { name: 'products.refNo', label: 'Product References', type: 'array', source: 'contracts', path: 'products', isVisible: true, isFilterable: true, isChartable: false }
    ],
    ['interactions']: [
      // Interaction fields
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'interactions', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'interactionType', label: 'Interaction Type', type: 'string', source: 'interactions', path: 'interactionType', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactionDate', label: 'Interaction Date', type: 'date', source: 'interactions', path: 'interactionDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactionTime', label: 'Interaction Time', type: 'string', source: 'interactions', path: 'interactionTime', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'interactionStatus', label: 'Interaction Status', type: 'string', source: 'interactions', path: 'interactionStatus', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'interactionNotes', label: 'Interaction Notes', type: 'string', source: 'interactions', path: 'interactionNotes', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriptionId', label: 'Subscription ID', type: 'string', source: 'interactions', path: 'subscriptionId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'documents', label: 'Documents', type: 'array', source: 'interactions', path: 'documents', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservationStatus', label: 'Reservation Status', type: 'string', source: 'interactions', path: 'reservationStatus', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reminderSentAt', label: 'Reminder Sent At', type: 'date', source: 'interactions', path: 'reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'interactions', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'interactions', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Reservation fields
      { name: 'reservation.refNo', label: 'Reservation Reference', type: 'string', source: 'interactions', path: 'reservationId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'reservation.status', label: 'Reservation Status', type: 'string', source: 'interactions', path: 'reservationId.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.expiryDate', label: 'Reservation Expiry Date', type: 'date', source: 'interactions', path: 'reservationId.expiryDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.reminderSentAt', label: 'Reservation Reminder Sent At', type: 'date', source: 'interactions', path: 'reservationId.reminderSentAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.createdAt', label: 'Reservation Created Date', type: 'date', source: 'interactions', path: 'reservationId.createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'reservation.updatedAt', label: 'Reservation Updated Date', type: 'date', source: 'interactions', path: 'reservationId.updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related User fields (from reservation)
      { name: 'user.refNo', label: 'User Reference', type: 'string', source: 'interactions', path: 'reservationId.userId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.title', label: 'User Title', type: 'string', source: 'interactions', path: 'reservationId.userId.title', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.firstName', label: 'User First Name', type: 'string', source: 'interactions', path: 'reservationId.userId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.lastName', label: 'User Last Name', type: 'string', source: 'interactions', path: 'reservationId.userId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.email', label: 'User Email', type: 'string', source: 'interactions', path: 'reservationId.userId.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.mobileNumber', label: 'User Mobile', type: 'string', source: 'interactions', path: 'reservationId.userId.mobileNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.companyName', label: 'User Company', type: 'string', source: 'interactions', path: 'reservationId.userId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'user.status', label: 'User Status', type: 'string', source: 'interactions', path: 'reservationId.userId.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'user.isActive', label: 'User Active', type: 'boolean', source: 'interactions', path: 'reservationId.userId.isActive', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Customer fields (from reservation)
      { name: 'customer.refNo', label: 'Customer Reference', type: 'string', source: 'interactions', path: 'reservationId.customerId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.businessName', label: 'Business Name', type: 'string', source: 'interactions', path: 'reservationId.customerId.businessName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.businessId', label: 'Business ID', type: 'string', source: 'interactions', path: 'reservationId.customerId.businessId', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'customer.zipCode', label: 'Customer Zip Code', type: 'string', source: 'interactions', path: 'reservationId.customerId.zipCode', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.industry', label: 'Industry', type: 'string', source: 'interactions', path: 'reservationId.customerId.industry', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.numberOfEmployees', label: 'Number of Employees', type: 'string', source: 'interactions', path: 'reservationId.customerId.numberOfEmployees', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.interestLevel', label: 'Interest Level', type: 'string', source: 'interactions', path: 'reservationId.customerId.interestLevel', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.acceptedPromotionalGifts', label: 'Accepted Promotional Gifts', type: 'boolean', source: 'interactions', path: 'reservationId.customerId.acceptedPromotionalGifts', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.address.city', label: 'Customer City', type: 'string', source: 'interactions', path: 'reservationId.customerId.address.city', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'customer.address.country', label: 'Customer Country', type: 'string', source: 'interactions', path: 'reservationId.customerId.address.country', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Products fields (from reservation)
      { name: 'products.refNo', label: 'Products Reference', type: 'array', source: 'interactions', path: 'reservationId.products.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.name', label: 'Products Name', type: 'array', source: 'interactions', path: 'reservationId.products.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.code', label: 'Products Code', type: 'array', source: 'interactions', path: 'reservationId.products.code', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.description', label: 'Products Description', type: 'array', source: 'interactions', path: 'reservationId.products.description', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.minPrice', label: 'Products Min Price', type: 'array', source: 'interactions', path: 'reservationId.products.minPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.maxPrice', label: 'Products Max Price', type: 'array', source: 'interactions', path: 'reservationId.products.maxPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.price', label: 'Products Price', type: 'array', source: 'interactions', path: 'reservationId.products.price', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.currency', label: 'Products Currency', type: 'array', source: 'interactions', path: 'reservationId.products.currency', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isActive', label: 'Products Active', type: 'array', source: 'interactions', path: 'reservationId.products.isActive', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Subscriber fields
      { name: 'subscriber.refNo', label: 'Subscriber Reference', type: 'string', source: 'interactions', path: 'subscriberId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.companyName', label: 'Subscriber Company', type: 'string', source: 'interactions', path: 'subscriberId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.firstName', label: 'Subscriber First Name', type: 'string', source: 'interactions', path: 'subscriberId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.lastName', label: 'Subscriber Last Name', type: 'string', source: 'interactions', path: 'subscriberId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.email', label: 'Subscriber Email', type: 'string', source: 'interactions', path: 'subscriberId.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.status', label: 'Subscriber Status', type: 'string', source: 'interactions', path: 'subscriberId.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Contracts fields (through reservation)
      { name: 'contracts.refNo', label: 'Contracts Reference', type: 'array', source: 'interactions', path: 'contracts.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'contracts.status', label: 'Contracts Status', type: 'array', source: 'interactions', path: 'contracts.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.contractValue', label: 'Contracts Value', type: 'array', source: 'interactions', path: 'contracts.contractValue', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.duration', label: 'Contracts Duration', type: 'array', source: 'interactions', path: 'contracts.duration', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.startDate', label: 'Contracts Start Date', type: 'array', source: 'interactions', path: 'contracts.startDate', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'contracts.endDate', label: 'Contracts End Date', type: 'array', source: 'interactions', path: 'contracts.endDate', isVisible: true, isFilterable: true, isChartable: true }
    ],
    ['territories']: [
      // Territory fields
      { name: 'refNo', label: 'Reference Number', type: 'string', source: 'territories', path: 'refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'name', label: 'Territory Name', type: 'string', source: 'territories', path: 'name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'region', label: 'Region', type: 'string', source: 'territories', path: 'region', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'status', label: 'Status', type: 'string', source: 'territories', path: 'status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'coordinates', label: 'Coordinates', type: 'array', source: 'territories', path: 'coordinates', isVisible: true, isFilterable: false, isChartable: false },
      { name: 'googlemapdata', label: 'Google Map Data', type: 'string', source: 'territories', path: 'googlemapdata', isVisible: false, isFilterable: false, isChartable: false },
      { name: 'createdAt', label: 'Created Date', type: 'date', source: 'territories', path: 'createdAt', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'updatedAt', label: 'Updated Date', type: 'date', source: 'territories', path: 'updatedAt', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Subscriber fields
      { name: 'subscriber.refNo', label: 'Subscriber Reference', type: 'string', source: 'territories', path: 'subscriberId.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.companyName', label: 'Subscriber Company', type: 'string', source: 'territories', path: 'subscriberId.companyName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.firstName', label: 'Subscriber First Name', type: 'string', source: 'territories', path: 'subscriberId.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.lastName', label: 'Subscriber Last Name', type: 'string', source: 'territories', path: 'subscriberId.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.email', label: 'Subscriber Email', type: 'string', source: 'territories', path: 'subscriberId.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'subscriber.status', label: 'Subscriber Status', type: 'string', source: 'territories', path: 'subscriberId.status', isVisible: true, isFilterable: true, isChartable: true },
      
      // Related Users fields (users assigned to this territory)
      { name: 'users.refNo', label: 'Users Reference', type: 'array', source: 'territories', path: 'users.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.firstName', label: 'Users First Name', type: 'array', source: 'territories', path: 'users.firstName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.lastName', label: 'Users Last Name', type: 'array', source: 'territories', path: 'users.lastName', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.email', label: 'Users Email', type: 'array', source: 'territories', path: 'users.email', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.mobileNumber', label: 'Users Mobile', type: 'array', source: 'territories', path: 'users.mobileNumber', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'users.status', label: 'Users Status', type: 'array', source: 'territories', path: 'users.status', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'users.isActive', label: 'Users Active', type: 'array', source: 'territories', path: 'users.isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'users.companyName', label: 'Users Company', type: 'array', source: 'territories', path: 'users.companyName', isVisible: true, isFilterable: true, isChartable: false },
      
      // Related Products fields (products available in this territory)
      { name: 'products.refNo', label: 'Products Reference', type: 'array', source: 'territories', path: 'products.refNo', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.name', label: 'Products Name', type: 'array', source: 'territories', path: 'products.name', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.code', label: 'Products Code', type: 'array', source: 'territories', path: 'products.code', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.description', label: 'Products Description', type: 'array', source: 'territories', path: 'products.description', isVisible: true, isFilterable: true, isChartable: false },
      { name: 'products.minPrice', label: 'Products Min Price', type: 'array', source: 'territories', path: 'products.minPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.maxPrice', label: 'Products Max Price', type: 'array', source: 'territories', path: 'products.maxPrice', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.price', label: 'Products Price', type: 'array', source: 'territories', path: 'products.price', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.currency', label: 'Products Currency', type: 'array', source: 'territories', path: 'products.currency', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isActive', label: 'Products Active', type: 'array', source: 'territories', path: 'products.isActive', isVisible: true, isFilterable: true, isChartable: true },
      { name: 'products.isArchived', label: 'Products Archived', type: 'array', source: 'territories', path: 'products.isArchived', isVisible: true, isFilterable: true, isChartable: true }
    ]
  };
  
  // Predefined report templates
  reportTemplates: SimpleReport[] = [
    {
      id: 'user-overview',
      name: 'User Overview',
      description: 'Overview of all users with key information',
      dataSource: 'users',
      fields: this.fieldDefinitions['users'].filter(f => ['firstName', 'lastName', 'email', 'status', 'isActive', 'createdAt'].includes(f.name)),
      filters: [],
      exportFormats: ['csv', 'excel', 'pdf'],
      isGlobal: true,
      createdAt: new Date()
    },
    {
      id: 'subscriber-analysis',
      name: 'Subscriber Analysis',
      description: 'Analysis of subscriber companies and their status',
      dataSource: 'subscribers',
      fields: this.fieldDefinitions['subscribers'],
      filters: [],
      exportFormats: ['csv', 'excel', 'pdf'],
      isGlobal: true,
      createdAt: new Date()
    },
    {
      id: 'customer-insights',
      name: 'Customer Insights',
      description: 'Customer data analysis by industry and location',
      dataSource: 'customers',
      fields: this.fieldDefinitions['customers'].filter(f => ['businessName', 'industry', 'numberOfEmployees', 'interestLevel', 'city', 'createdAt'].includes(f.name)),
      filters: [],
      exportFormats: ['csv', 'excel', 'pdf'],
      isGlobal: true,
      createdAt: new Date()
    },
    {
      id: 'product-performance',
      name: 'Product Performance',
      description: 'Product analysis with pricing and status information',
      dataSource: 'products',
      fields: this.fieldDefinitions['products'].filter(f => ['name', 'minPrice', 'maxPrice', 'currency', 'isActive', 'createdAt'].includes(f.name)),
      filters: [],
      exportFormats: ['csv', 'excel', 'pdf'],
      isGlobal: true,
      createdAt: new Date()
    },
    {
      id: 'reservation-tracking',
      name: 'Reservation Tracking',
      description: 'Track reservation status and expiry dates',
      dataSource: 'reservations',
      fields: this.fieldDefinitions['reservations'],
      filters: [],
      exportFormats: ['csv', 'excel', 'pdf'],
      isGlobal: true,
      createdAt: new Date()
    },
    {
      id: 'contract-value-analysis',
      name: 'Contract Value Analysis',
      description: 'Analysis of contract values and durations',
      dataSource: 'contracts',
      fields: this.fieldDefinitions['contracts'].filter(f => ['contractValue', 'duration', 'status', 'startDate', 'endDate', 'createdAt'].includes(f.name)),
      filters: [],
      exportFormats: ['csv', 'excel', 'pdf'],
      isGlobal: true,
      createdAt: new Date()
    }
  ];
  
  // Filter operators
  filterOperators = [
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'not_equals' },
    { label: 'Contains', value: 'contains' },
    { label: 'Greater Than', value: 'greater_than' },
    { label: 'Less Than', value: 'less_than' },
    { label: 'Between', value: 'between' },
    { label: 'Is Null', value: 'is_null' },
    { label: 'Is Not Null', value: 'is_not_null' }
  ];
  
  // Chart types
  chartTypes = [
    { label: 'Bar Chart', value: 'bar', icon: 'pi pi-chart-bar' },
    { label: 'Line Chart', value: 'line', icon: 'pi pi-chart-line' },
    { label: 'Pie Chart', value: 'pie', icon: 'pi pi-chart-pie' },
    { label: 'Doughnut Chart', value: 'doughnut', icon: 'pi pi-circle' }
  ];
  
  // Export formats
  exportFormats = [
    { label: 'CSV', value: 'csv', icon: 'pi pi-file' },
    { label: 'Excel', value: 'excel', icon: 'pi pi-file-excel' },
    { label: 'PDF', value: 'pdf', icon: 'pi pi-file-pdf' }
  ];

  // Export menu items
  exportMenuItems: any[] = [];

  // Related sections state
  openRelatedSections: { [key: string]: boolean } = {};

  // Static Reports Properties
  activeTab: 'dynamic' | 'static' = 'static';
  selectedStaticReport: string | null = null;
  staticReportData: any = null;
  currentPageStatic = 1;
  pageSizeStatic = 10;
  totalRecordsStatic = 0;
  
  // Static report configurations - imported from shared config
  staticReports: any[] = STATIC_REPORTS_CONFIG;
  
  // Filters for static reports
  selectedMonth: number = new Date().getMonth() + 1;
  selectedYear: number = new Date().getFullYear();
  months: { label: string; value: number }[] = [];
  years: number[] = [];
  personalPipelineType: 'active' | 'expired' = 'active';
  monthlyWinLossType: 'wins' | 'losses' = 'wins';

  // Current user for role checking
  currentUser: any = null;
  selectedSubscriberId: string | null = null;

  constructor(
    private customReportService: CustomReportService,
    private staticReportService: StaticReportService,
    private authService: AuthService,
    private subscriptionContextService: SubscriptionContextService,
    private fb: FormBuilder,
    public customMessageService: CustomMessageService,
    private globalLoadingService: GlobalLoadingService) {
    this.initializeForms();
    this.initializeYears();
  }

  ngOnInit() {
    // this.loadReports();
    this.loadSavedReports();
    this.initializeExportMenuItems();
    this.initializeStaticExportMenuItems();
    this.loadCurrentUser();
    this.loadSubscriberContext();
  }

  private loadCurrentUser() {
    try {
      this.currentUser = this.authService.getLoggedInUser();
      console.log('Current user loaded:', this.currentUser);
    } catch (error) {
      console.error('Error loading current user:', error);
      this.currentUser = null;
    }
  }

  private loadSubscriberContext() {
    try {
      this.selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
      console.log('Selected subscriber ID loaded:', this.selectedSubscriberId);
    } catch (error) {
      console.error('Error loading subscriber context:', error);
      this.selectedSubscriberId = null;
    }
  }
  
  private initializeYears() {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      this.years.push(i);
    }
    
    // Initialize months with labels
    for (let i = 1; i <= 12; i++) {
      const date = new Date(2024, i - 1);
      this.months.push({
        label: date.toLocaleString('en-US', { month: 'long' }),
        value: i
      });
    }
  }

  private initializeForms() {
    this.reportForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', Validators.maxLength(500)],
      dataSource: ['', Validators.required],
      isGlobal: [false]
    });

    this.filterForm = this.fb.group({
      field: [''],
      operator: ['equals'],
      value: [''],
      secondValue: ['']
    });
  }

  // Load predefined reports
  // loadReports() {
  //   this.reports = [...this.reportTemplates];
  // }

  // Load saved reports
  async loadSavedReports() {
    await this.globalLoadingService.executeWithLoading(async () => {
      try {
        console.log('Loading saved reports...');
        this.savedReports = await this.customReportService.getSavedSimpleReports();
        console.log('Loaded saved reports:', this.savedReports);
      } catch (error: any) {
        console.error('Error loading saved reports:', error);
        this.customMessageService.error('Error', error.message || 'Failed to load saved reports');
      }
    });
  }

  // Create new report
  createNewReport() {
    this.currentReport = null;
    this.resetForms();
    this.customMessageService.clearAll(); // Clear all messages when opening new report
    this.reportBuilderVisible = true;
  }

  // Use predefined template
  useTemplate(template: SimpleReport) {
    this.currentReport = { ...template };
    this.selectedDataSource = template.dataSource;
    this.selectedFields = [...template.fields];
    this.selectedFilters = [...template.filters];
    this.customMessageService.clearAll(); // Clear messages when using template
    this.reportForm.patchValue({
      name: template.name,
      description: template.description,
      dataSource: template.dataSource,
      isGlobal: template.isGlobal
    });
    this.reportBuilderVisible = true;
  }

  // Data source selection
  selectDataSource(value: string) {
    this.reportForm.get('dataSource')?.setValue(value);
    this.onDataSourceChange();
  }

  onDataSourceChange() {
    const dataSource = this.reportForm.get('dataSource')?.value;
    this.selectedDataSource = dataSource;
    this.selectedFields = [];
    this.selectedFilters = [];
    this.chartConfig = null;
  }

  // Field selection
  get availableFields(): ReportField[] {
    return this.fieldDefinitions[this.selectedDataSource] || [];
  }

  // Get related models for current data source
  getRelatedModels(): string[] {
    const relationships = this.modelRelationships[this.selectedDataSource];
    return relationships ? Object.values(relationships) : [];
  }

  // Check if a field is from a related model
  isRelatedField(field: ReportField): boolean {
    return !!(field && field.name && field.name.includes('.') && !field.name.startsWith('address.'));
  }

  // Get the related model name for a field
  getRelatedModelName(field: ReportField): string {
    if (!this.isRelatedField(field) || !field || !field.name) return '';
    const parts = field.name.split('.');
    return parts[0]; // e.g., 'user', 'customer', 'products'
  }

  // Group fields by category (main fields vs related fields)
  getGroupedFields(): { main: ReportField[]; related: { [key: string]: ReportField[] } } {
    const fields = this.availableFields;
    const grouped: { main: ReportField[]; related: { [key: string]: ReportField[] } } = {
      main: [],
      related: {}
    };

    fields.forEach(field => {
      if (this.isRelatedField(field)) {
        const relatedModel = this.getRelatedModelName(field);
        if (!grouped.related[relatedModel]) {
          grouped.related[relatedModel] = [];
        }
        grouped.related[relatedModel].push(field);
      } else {
        grouped.main.push(field);
      }
    });

    return grouped;
  }

  // Get keys from object (for template use)
  getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  // Related sections management
  toggleRelatedSection(relatedModel: string) {
    this.openRelatedSections[relatedModel] = !this.openRelatedSections[relatedModel];
  }

  isRelatedSectionOpen(relatedModel: string): boolean {
    return this.openRelatedSections[relatedModel] || false;
  }

  toggleFieldSelection(field: ReportField) {
    const index = this.selectedFields.findIndex(f => f.name === field.name);
    if (index > -1) {
      this.selectedFields.splice(index, 1);
    } else {
      this.selectedFields.push(field);
    }
    // Update export menu items when fields change
    this.updateExportMenuItems();
    // Clear chart data/errors when fields change as chart config may become invalid
    this.chartData = null;
    this.chartError = null;
  }

  isFieldSelected(field: ReportField): boolean {
    return this.selectedFields.some(f => f.name === field.name);
  }

  removeSelectedField(field: ReportField) {
    const index = this.selectedFields.findIndex(f => f.name === field.name);
    if (index > -1) {
      this.selectedFields.splice(index, 1);
    }
    // Update export menu items when fields change
    this.updateExportMenuItems();
    // Clear chart data/errors when fields change as chart config may become invalid
    this.chartData = null;
    this.chartError = null;
  }

  updateExportMenuItems() {
    this.exportMenuItems = [
      {
        label: 'CSV',
        icon: 'pi pi-file',
        command: () => this.exportReport('csv')
      },
      {
        label: 'Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportReport('excel')
      },
      {
        label: 'PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.exportReport('pdf'),
        disabled: this.selectedFields.length > 8, // Disable if too many fields
        tooltip: this.selectedFields.length > 8 ? 'PDF export limited to 8 fields. Use CSV or Excel for more fields.' : ''
      }
    ];
  }

  // Date range filter properties
  dateRangeFilter = {
    enabled: false,
    field: '',
    startDate: null as Date | null,
    endDate: null as Date | null
  };

  // Filter management
  addFilter() {
    if (this.selectedFields.length === 0) {
      this.customMessageService.warning('Warning', 'Please select fields first before adding filters');
      return;
    }

    const filterValue = this.filterForm.value;
    const field = this.selectedFields.find(f => f.name === filterValue.field);
    
    if (!field) return;

    const newFilter: ReportFilter = {
      field: filterValue.field,
      label: field.label,
      type: field.type,
      operator: filterValue.operator,
      value: filterValue.value,
      options: this.getFilterOptions(field)
    };

    this.selectedFilters.push(newFilter);
    this.filterForm.reset({ operator: 'equals' });
  }

  removeFilter(index: number) {
    this.selectedFilters.splice(index, 1);
  }

  // Date range filter methods
  getDateFields(): ReportField[] {
    return this.selectedFields.filter(field => field.type === 'date');
  }

  hasDateFields(): boolean {
    return this.getDateFields().length > 0;
  }

  enableDateRangeFilter() {
    this.dateRangeFilter.enabled = true;
    // Set the first date field as default if none selected
    if (!this.dateRangeFilter.field && this.getDateFields().length > 0) {
      this.dateRangeFilter.field = this.getDateFields()[0].name;
    }
  }

  disableDateRangeFilter() {
    this.dateRangeFilter.enabled = false;
    this.dateRangeFilter.field = '';
    this.dateRangeFilter.startDate = null;
    this.dateRangeFilter.endDate = null;
  }

  getDateRangeFilterField(): ReportField | null {
    if (!this.dateRangeFilter.field) return null;
    return this.selectedFields.find(f => f.name === this.dateRangeFilter.field) || null;
  }

  isDateRangeFilterValid(): boolean {
    if (!this.dateRangeFilter.enabled) return true;
    
    // If enabled, check if all required values are provided
    if (!this.dateRangeFilter.field) return false;
    if (!this.dateRangeFilter.startDate || !this.dateRangeFilter.endDate) return false;
    if (this.dateRangeFilter.startDate > this.dateRangeFilter.endDate) return false;
    
    return true;
  }

  getSelectedFieldForFilter(): ReportField | null {
    const fieldName = this.filterForm.get('field')?.value;
    if (!fieldName) return null;
    return this.selectedFields.find(f => f.name === fieldName) || null;
  }

  hasPredefinedOptions(): boolean {
    const field = this.getSelectedFieldForFilter();
    return field ? this.getFilterOptions(field).length > 0 : false;
  }

  getFilterOptions(field: ReportField | null): any[] {
    if (!field) return [];
    
    // Return predefined options for specific fields using common settings
    const optionsMap: { [key: string]: any[] } = {
      // User status fields
      'status': Object.values(COMMON_SETTINGS.USER_STATUS).map(status => ({ label: status, value: status })),
      'user.status': Object.values(COMMON_SETTINGS.USER_STATUS).map(status => ({ label: status, value: status })),
      
      // Boolean fields
      'isActive': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      'user.isActive': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      'isArchived': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      'user.isArchived': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      'isFirstLogin': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      'user.isFirstLogin': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      'acceptedPromotionalGifts': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      'customer.acceptedPromotionalGifts': [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      
      // Gender fields
      'gender': Object.values(COMMON_SETTINGS.USER_GENDER).map(gender => ({ label: gender, value: gender })),
      'user.gender': Object.values(COMMON_SETTINGS.USER_GENDER).map(gender => ({ label: gender, value: gender })),
      
      // Trade licence fields
      'tradeLicence': Object.values(COMMON_SETTINGS.TRADE_LICENCE).map(licence => ({ label: licence, value: licence })),
      'user.tradeLicence': Object.values(COMMON_SETTINGS.TRADE_LICENCE).map(licence => ({ label: licence, value: licence })),
      
      // Reservation status fields
      'reservationStatus': Object.values(COMMON_SETTINGS.RESERVATION_STATUS).map(status => ({ label: status, value: status })),
      
      // Contract status fields
      'contractStatus': Object.values(COMMON_SETTINGS.CONTRACT_STATUS).map(status => ({ label: status, value: status })),
      
      // Subscriber status fields
      'subscriberStatus': Object.values(COMMON_SETTINGS.SUBSCRIBER_STATUS).map(status => ({ label: status, value: status })),
      
      // Additional predefined options
      'citizenship': [
        { label: 'UAE', value: 'UAE' },
        { label: 'Indian', value: 'Indian' },
        { label: 'Pakistani', value: 'Pakistani' },
        { label: 'Filipino', value: 'Filipino' },
        { label: 'Egyptian', value: 'Egyptian' },
        { label: 'Other', value: 'Other' }
      ],
      'user.citizenship': [
        { label: 'UAE', value: 'UAE' },
        { label: 'Indian', value: 'Indian' },
        { label: 'Pakistani', value: 'Pakistani' },
        { label: 'Filipino', value: 'Filipino' },
        { label: 'Egyptian', value: 'Egyptian' },
        { label: 'Other', value: 'Other' }
      ],
      'industry': [
        { label: 'Healthcare', value: 'Healthcare' },
        { label: 'Education', value: 'Education' },
        { label: 'Technology', value: 'Technology' },
        { label: 'Finance', value: 'Finance' },
        { label: 'Retail', value: 'Retail' },
        { label: 'Manufacturing', value: 'Manufacturing' },
        { label: 'Other', value: 'Other' }
      ],
      'customer.industry': [
        { label: 'Healthcare', value: 'Healthcare' },
        { label: 'Education', value: 'Education' },
        { label: 'Technology', value: 'Technology' },
        { label: 'Finance', value: 'Finance' },
        { label: 'Retail', value: 'Retail' },
        { label: 'Manufacturing', value: 'Manufacturing' },
        { label: 'Other', value: 'Other' }
      ],
      'numberOfEmployees': [
        { label: '1-10', value: '1-10' },
        { label: '11-50', value: '11-50' },
        { label: '51-200', value: '51-200' },
        { label: '201-500', value: '201-500' },
        { label: '500+', value: '500+' }
      ],
      'customer.numberOfEmployees': [
        { label: '1-10', value: '1-10' },
        { label: '11-50', value: '11-50' },
        { label: '51-200', value: '51-200' },
        { label: '201-500', value: '201-500' },
        { label: '500+', value: '500+' }
      ],
      'interestLevel': [
        { label: 'High', value: 'High' },
        { label: 'Medium', value: 'Medium' },
        { label: 'Low', value: 'Low' }
      ],
      'customer.interestLevel': [
        { label: 'High', value: 'High' },
        { label: 'Medium', value: 'Medium' },
        { label: 'Low', value: 'Low' }
      ],
      'interactionType': [
        { label: 'Call', value: 'Call' },
        { label: 'Email', value: 'Email' },
        { label: 'Meeting', value: 'Meeting' },
        { label: 'Visit', value: 'Visit' }
      ],
      'interactionStatus': [
        { label: 'Completed', value: 'Completed' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Cancelled', value: 'Cancelled' }
      ]
    };

    return optionsMap[field.name] || [];
  }

  // Chart configuration
  initializeChart() {
    if (!this.chartConfig) {
      this.chartConfig = {
        enabled: true,
        type: 'bar',
        xAxis: '',
        yAxis: [],
        title: this.reportForm.get('name')?.value || 'Chart'
      };
    }
    // Clear chart error when initializing
    this.chartError = null;
  }

  // Get chart validation status and error message
  getChartValidationStatus(): { isValid: boolean; error?: string; warning?: string } {
    if (!this.chartConfig?.enabled) {
      return { isValid: true };
    }

    if (!this.chartConfig.xAxis || this.chartConfig.xAxis.trim() === '') {
      return { 
        isValid: false, 
        error: 'X-axis field is required. Please select a categorical field (string, date, or boolean) to group your data.' 
      };
    }

    const xAxisField = this.selectedFields.find(f => f.name === this.chartConfig?.xAxis);
    if (!xAxisField) {
      return { 
        isValid: false, 
        error: 'Selected X-axis field is not available. Please select a different field.' 
      };
    }

    if (xAxisField.type !== 'string' && xAxisField.type !== 'date' && xAxisField.type !== 'boolean') {
      return { 
        isValid: false, 
        error: `X-axis field "${xAxisField.label}" must be categorical (string, date, or boolean). Current type: ${xAxisField.type}.` 
      };
    }

    // Check Y-axis fields if selected
    if (this.chartConfig.yAxis && this.chartConfig.yAxis.length > 0) {
      for (const yField of this.chartConfig.yAxis) {
        const yAxisField = this.selectedFields.find(f => f.name === yField);
        if (!yAxisField) {
          return { 
            isValid: false, 
            error: `Y-axis field "${yField}" is not available in selected fields.` 
          };
        }
        if (!yAxisField.isChartable) {
          return { 
            isValid: false, 
            error: `Y-axis field "${yAxisField.label}" is not chartable. Select numeric or countable fields for Y-axis.` 
          };
        }
      }
    }

    return { isValid: true };
  }

  getChartableFields(): ReportField[] {
    return this.selectedFields.filter(f => f.isChartable);
  }

  // Get fields suitable for X-axis (categorical fields)
  getCategoricalFields(): ReportField[] {
    return this.selectedFields.filter(f => 
      f.type === 'string' || f.type === 'date' || f.type === 'boolean'
    );
  }

  // Get fields suitable for Y-axis (numerical fields)
  getNumericalFields(): ReportField[] {
    return this.selectedFields.filter(f => f.type === 'number');
  }

  canCreateChart(): boolean {
    return this.getCategoricalFields().length > 0;
  }

  canCreateChartWithNumericalData(): boolean {
    return this.getCategoricalFields().length > 0 && this.getNumericalFields().length > 0;
  }

  // Execute report
  async executeReport() {
    if (!this.validateReport()) {
      this.customMessageService.warning('Validation Failed', 'Please check your report configuration');
      return;
    }

    try {
      this.loading = true;
      
      // Build report configuration
      const reportConfig = this.buildReportConfig();
      console.log('Executing report with config:', reportConfig);
      
      // Execute the report
      this.reportData = await this.customReportService.executeSimpleReport(reportConfig);
      
      if (!this.reportData || !this.reportData.data) {
        throw new Error('No data returned from server');
      }
      
      // Generate chart data if configured
      if (this.chartConfig?.enabled && this.canCreateChart()) {
        const chartResult = this.generateChartData();
        if (!chartResult.success) {
          this.chartError = chartResult.error || 'Failed to generate chart data';
          this.customMessageService.warning('Chart Generation Warning', chartResult.error || 'Chart could not be generated. Please check your chart configuration.');
        } else {
          this.chartError = null;
        }
      } else if (this.chartConfig?.enabled) {
        this.chartError = 'Cannot create chart: No categorical fields available. Please select at least one categorical field (string, date, or boolean) to use as the X-axis.';
        this.customMessageService.warning('Chart Configuration Error', this.chartError);
      }
      
      this.reportExecutionVisible = true;
      
      // this.customMessageService.success('Report Executed', `Report executed successfully. Found ${this.reportData.data.length} records.`);
      
    } catch (error: any) {
      console.error('Report execution error:', error);
      this.customMessageService.error('Execution Failed', error.message || 'Failed to execute report');
    } finally {
      this.loading = false;
    }
  }

  private buildReportConfig(): any {
    // Validate that we have the necessary data
    if (!this.selectedDataSource) {
      console.error('No data source selected');
      throw new Error('No data source selected');
    }
    
    if (!this.selectedFields || this.selectedFields.length === 0) {
      console.error('No fields selected');
      throw new Error('No fields selected');
    }
    
    const config = {
      name: this.reportForm.get('name')?.value,
      description: this.reportForm.get('description')?.value,
      dataSource: this.selectedDataSource,
      fields: (this.selectedFields || []).map(f => {
        if (!f || !f.name) {
          console.warn('Invalid field in selectedFields:', f);
          return null;
        }
        return f.name;
      }).filter(f => f !== null), // Remove any null fields
      filters: this.selectedFilters || [],
      dateRangeFilter: this.dateRangeFilter.enabled ? {
        field: this.dateRangeFilter.field,
        startDate: this.dateRangeFilter.startDate,
        endDate: this.dateRangeFilter.endDate
      } : null,
      chartConfig: this.chartConfig,
      page: this.currentPage || 1, // Ensure page is never null
      limit: this.pageSize || 10   // Ensure limit has a default
    };
    
    console.log('Report config being sent:', config); // Debug log
    console.log('Selected fields:', this.selectedFields); // Debug log
    console.log('Selected data source:', this.selectedDataSource); // Debug log
    return config;
  }

  private generateChartData(): { success: boolean; error?: string } {
    // Clear previous errors
    this.chartError = null;
    
    if (!this.reportData?.data) {
      return { success: false, error: 'No report data available. Please execute the report first.' };
    }

    if (!this.chartConfig) {
      return { success: false, error: 'Chart configuration is missing.' };
    }

    const data = this.reportData.data;
    const xField = this.chartConfig.xAxis;
    const yFields = this.chartConfig.yAxis || [];

    if (!xField || xField.trim() === '') {
      return { success: false, error: 'X-axis field is not selected. Please select a categorical field (string, date, or boolean) for the X-axis in the chart configuration.' };
    }

    // Check if X-axis field exists in the data
    if (data.length > 0 && !(xField in data[0])) {
      const xFieldLabel = this.getFieldLabel(xField);
      return { success: false, error: `The X-axis field "${xFieldLabel}" does not exist in the report data. Please select a different field or ensure the field is included in your selected fields.` };
    }

    // Check if data is empty
    if (data.length === 0) {
      return { success: false, error: 'No data available to display in the chart. Your filters may be too restrictive, or there is no data matching your criteria.' };
    }

    try {
      // Optimized: Single-pass grouping using Map for O(n) complexity instead of O(n²)
      const groupedData = new Map<string, any[]>();
      
      // Single pass to group data by xField value
      for (const row of data) {
        const key = String(row[xField] || 'Unknown');
        if (!groupedData.has(key)) {
          groupedData.set(key, []);
        }
        groupedData.get(key)!.push(row);
      }
      
      // Get unique labels in order
      const labels = Array.from(groupedData.keys());
      
      // Check if we have any groups
      if (labels.length === 0) {
        return { success: false, error: 'No data groups found. The X-axis field may not have any values, or all values are null/empty.' };
      }
      
      let datasets;
      
      if (yFields.length > 0) {
        // Chart with chartable data
        // Pre-cache field definitions to avoid repeated lookups
        const fieldDefs = new Map<string, any>();
        yFields.forEach(yField => {
          const fieldDef = this.selectedFields.find(f => f.name === yField);
          if (!fieldDef) {
            throw new Error(`Y-axis field "${yField}" not found in selected fields.`);
          }
          fieldDefs.set(yField, fieldDef);
        });
        
        datasets = yFields.map((yField, index) => {
          const fieldDef = fieldDefs.get(yField);
          if (!fieldDef) {
            throw new Error(`Y-axis field "${yField}" definition not found.`);
          }
          
          // Check if Y-axis field exists in the data
          if (data.length > 0 && !(yField in data[0])) {
            throw new Error(`Y-axis field "${fieldDef.label}" does not exist in the report data.`);
          }
          
          const isNumeric = fieldDef.type === 'number';
          
          // Build data array in single pass
          const fieldData = labels.map(label => {
            const rows = groupedData.get(label)!;
            
            if (isNumeric) {
              // For numerical fields, sum the values
              return rows.reduce((sum, row) => sum + (parseFloat(row[yField]) || 0), 0);
            } else {
              // For other chartable fields, count occurrences
              return rows.length;
            }
          });

          const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];
          
          return {
            label: this.getFieldLabel(yField),
            data: fieldData,
            backgroundColor: colors[index % colors.length],
            borderColor: colors[index % colors.length],
            borderWidth: this.chartConfig?.type === 'line' ? 2 : 1
          };
        });
      } else {
        // Chart with record counts only - use pre-grouped data
        const fieldData = labels.map(label => groupedData.get(label)!.length);

        datasets = [{
          label: 'Record Count',
          data: fieldData,
          backgroundColor: '#3b82f6',
          borderColor: '#3b82f6',
          borderWidth: this.chartConfig?.type === 'line' ? 2 : 1
        }];
      }

      this.chartData = {
        labels,
        datasets
      };

      this.generateChartOptions();
      
      return { success: true };
      
    } catch (error: any) {
      console.error('Error generating chart data:', error);
      return { success: false, error: error.message || 'An error occurred while generating the chart. Please check your chart configuration and try again.' };
    }
  }

  private generateChartOptions() {
    if (!this.chartConfig) return;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: this.chartConfig.title
        },
        legend: {
          display: true,
          position: 'top'
        }
      }
    };

    if (this.chartConfig.type === 'line') {
      this.chartOptions.scales = {
        y: { beginAtZero: true },
        x: { grid: { display: false } }
      };
    }
  }

  // Export functionality
  async exportReport(format: 'csv' | 'excel' | 'pdf') {
    if (!this.reportData?.data?.length) {
      this.customMessageService.warning('No Data', 'No data available to export');
      return;
    }

    try {
      this.loading = true;
      
      // Build report configuration for export
      const reportConfig = this.buildReportConfig();
      
      console.log('Starting export with config:', reportConfig);
      
      // Check for PDF field count warning
      if (format === 'pdf' && reportConfig.fields.length > 10) {
        this.customMessageService.warning('PDF Display Warning', `PDF export with ${reportConfig.fields.length} fields may not display correctly due to space constraints. Consider using CSV or Excel export for better readability.`, 10000);
      }
      
      // Use the new direct export method
      await this.customReportService.exportSimpleReportDirect(reportConfig, format);
      
      this.customMessageService.success('Export Completed', `Report exported to ${format.toUpperCase()} successfully`);
      
    } catch (error: any) {
      console.error('Export error:', error);
      
      // Check if it's a PDF field limit error
      if (format === 'pdf' && error.message?.includes('Too many fields')) {
        this.customMessageService.warning('PDF Export Limited', error.message + ' Try using CSV or Excel export for reports with many fields.', 8000);
      } else {
        this.customMessageService.error('Export Failed', error.message || 'Failed to export report. Please try again.');
      }
    } finally {
      this.loading = false;
    }
  }

  // Pagination
  async onPageChange(event: any) {
    console.log('Page change event:', event); // Debug log
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    console.log('Current page:', this.currentPage, 'Page size:', this.pageSize); // Debug log
    await this.executeReport();
  }

  // Helper Methods for Template
  formatExecutionTime(executionTime?: number): string {
    if (!executionTime) return '0ms';
    return executionTime < 1000 ? `${executionTime}ms` : `${(executionTime / 1000).toFixed(2)}s`;
  }

  // Math object for template
  Math = Math;

  getDataSourceIcon(dataSource: string): string {
    const source = this.dataSourceOptions.find(ds => ds.value === dataSource);
    return source?.icon || 'pi pi-file';
  }


  getDataSources(): any[] {
    return this.dataSourceOptions;
  }

  getSelectedFieldsOptions(): any[] {
    return this.selectedFields.map(f => ({ label: f.label, value: f.name }));
  }

  getFilterOperators(): any[] {
    return this.filterOperators;
  }

  getChartTypes(): any[] {
    return this.chartTypes;
  }

  getChartableFieldsOptions(): any[] {
    return this.getChartableFields().map(f => ({ label: f.label, value: f.name }));
  }

  getCategoricalFieldsOptions(): any[] {
    return this.getCategoricalFields().map(f => ({ label: f.label, value: f.name }));
  }

  getNumericalFieldsOptions(): any[] {
    return this.getNumericalFields().map(f => ({ label: f.label, value: f.name }));
  }

  private initializeExportMenuItems() {
    this.updateExportMenuItems();
  }

  getExportMenuItems(): any[] {
    return this.exportMenuItems;
  }

  isReportValid(): boolean {
    return this.validateReportSilent();
  }

  // Silent validation for template use (doesn't show messages)
  private validateReportSilent(): boolean {
    if (this.reportForm.invalid) {
      return false;
    }

    if (this.selectedFields.length === 0) {
      return false;
    }

    return true;
  }

  getFieldTypeIcon(type: string): string {
    switch (type) {
      case 'string': return 'pi pi-font';
      case 'number': return 'pi pi-hashtag';
      case 'date': return 'pi pi-calendar';
      case 'boolean': return 'pi pi-check';
      case 'array': return 'pi pi-list';
      default: return 'pi pi-tag';
    }
  }

  // Chart config update methods
  updateChartEnabled(enabled: boolean) {
    if (!this.chartConfig) {
      this.chartConfig = {
        enabled: false,
        type: 'bar',
        xAxis: '',
        yAxis: [],
        title: ''
      };
    }
    this.chartConfig.enabled = enabled;
    // Clear chart data and error when chart is disabled
    if (!enabled) {
      this.chartData = null;
      this.chartError = null;
    }
  }

  updateChartType(type: string) {
    if (!this.chartConfig) {
      this.chartConfig = {
        enabled: false,
        type: 'bar',
        xAxis: '',
        yAxis: [],
        title: ''
      };
    }
    this.chartConfig.type = type as 'line' | 'bar' | 'pie' | 'doughnut';
    // Regenerate chart options when type changes
    if (this.chartData) {
      this.generateChartOptions();
    }
  }

  updateChartXAxis(xAxis: string) {
    if (!this.chartConfig) {
      this.chartConfig = {
        enabled: false,
        type: 'bar',
        xAxis: '',
        yAxis: [],
        title: ''
      };
    }
    this.chartConfig.xAxis = xAxis;
    // Clear chart data and error when X-axis changes
    this.chartData = null;
    this.chartError = null;
  }

  updateChartYAxis(yAxis: string[]) {
    if (!this.chartConfig) {
      this.chartConfig = {
        enabled: false,
        type: 'bar',
        xAxis: '',
        yAxis: [],
        title: ''
      };
    }
    this.chartConfig.yAxis = yAxis;
    // Clear chart data and error when Y-axis changes
    this.chartData = null;
    this.chartError = null;
  }

  updateChartTitle(title: string) {
    if (!this.chartConfig) {
      this.chartConfig = {
        enabled: false,
        type: 'bar',
        xAxis: '',
        yAxis: [],
        title: ''
      };
    }
    this.chartConfig.title = title;
  }

  // Validation
  private validateReport(): boolean {
    if (this.reportForm.invalid) {
      this.customMessageService.warning('Validation Error', 'Please fill in all required fields');
      return false;
    }

    if (this.selectedFields.length === 0) {
      this.customMessageService.warning('Validation Error', 'Please select at least one field');
      return false;
    }

    if (!this.isDateRangeFilterValid()) {
      this.customMessageService.warning('Validation Error', 'Please provide valid date range filter values');
      return false;
    }

    // Validate chart configuration if enabled
    if (this.chartConfig?.enabled) {
      if (!this.chartConfig.xAxis || this.chartConfig.xAxis.trim() === '') {
        this.customMessageService.warning('Chart Configuration Error', 'Please select an X-axis field (categorical field) for the chart. The X-axis groups your data into categories.');
        return false;
      }

      // Check if X-axis field exists in selected fields
      const xAxisField = this.selectedFields.find(f => f.name === this.chartConfig?.xAxis);
      if (!xAxisField) {
        this.customMessageService.warning('Chart Configuration Error', 'The selected X-axis field is not available in your selected fields. Please select a different X-axis field.');
        return false;
      }

      // Check if X-axis field is categorical
      if (xAxisField.type !== 'string' && xAxisField.type !== 'date' && xAxisField.type !== 'boolean') {
        this.customMessageService.warning('Chart Configuration Error', `The X-axis field "${xAxisField.label}" must be a categorical field (string, date, or boolean). Current type: ${xAxisField.type}. Please select a different field for the X-axis.`);
        return false;
      }

      // Validate Y-axis fields if selected
      if (this.chartConfig.yAxis && this.chartConfig.yAxis.length > 0) {
        for (const yField of this.chartConfig.yAxis) {
          const yAxisField = this.selectedFields.find(f => f.name === yField);
          if (!yAxisField) {
            this.customMessageService.warning('Chart Configuration Error', `The Y-axis field "${yField}" is not available in your selected fields. Please remove it or select a different field.`);
            return false;
          }
          if (!yAxisField.isChartable) {
            this.customMessageService.warning('Chart Configuration Error', `The Y-axis field "${yAxisField.label}" is not chartable. Please select chartable fields (numeric or countable fields) for the Y-axis.`);
            return false;
          }
        }
      }
    }

    return true;
  }

  // Method to trigger validation on button click
  triggerValidation() {
    // Clear existing validation messages first
    this.customMessageService.clearAll();
    
    // Then run validation with messages
    this.validateReport();
  }

  // Helper methods
  private resetForms() {
    this.reportForm.reset();
    this.filterForm.reset({ operator: 'equals' });
    this.selectedFields = [];
    this.selectedFilters = [];
    this.chartConfig = null;
    this.dateRangeFilter = {
      enabled: false,
      field: '',
      startDate: null,
      endDate: null
    };
  }

  getFieldLabel(fieldName: string): string {
    const field = this.selectedFields.find(f => f.name === fieldName);
    return field?.label || fieldName;
  }

  formatValue(value: any, field: ReportField): string {
    if (value === null || value === undefined) return '';
    
    if (field.type === 'date') {
      return new Date(value).toLocaleDateString();
    }
    
    if (field.type === 'number') {
      return new Intl.NumberFormat('de-DE').format(value);
    }
    
    if (field.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return value.toString();
  }

  toggleChartView() {
    this.chartViewVisible = !this.chartViewVisible;
  }

  closeDialogs() {
    // this.reportBuilderVisible = false;
    this.reportExecutionVisible = false;
    this.chartViewVisible = false;
  }

  // Save/Load Report functionality
  async saveReportFromForm() {
    const formValue = this.reportForm.value;
    
    if (!formValue.name || formValue.name.trim() === '') {
      this.customMessageService.warning('Validation Error', 'Report name is required');
      return;
    }

    try {
      this.loading = true;
      
      const reportConfig = this.buildReportConfig();
      const reportData = {
        name: formValue.name,
        description: formValue.description || '',
        dataSource: reportConfig.dataSource,
        fields: reportConfig.fields,
        filters: reportConfig.filters,
        dateRangeFilter: reportConfig.dateRangeFilter,
        chartConfig: reportConfig.chartConfig,
        isPublic: this.isPublic
      };

      const savedReport = await this.customReportService.saveSimpleReport(reportData);
      
      this.customMessageService.success('Report Saved', `Report "${formValue.name}" saved successfully`);

      await this.loadSavedReports();
    } catch (error: any) {
      console.error('Save report error:', error);
      this.customMessageService.error('Save Failed', error.message || 'Failed to save report');
    } finally {
      this.loading = false;
    }
  }

  openSaveDialog() {
    this.reportName = '';
    this.reportDescription = '';
    this.isPublic = false;
    this.showSaveDialog = true;
  }

  async saveReport() {
    if (!this.reportName || this.reportName.trim() === '') {
      this.customMessageService.warning('Validation Error', 'Report name is required');
      return;
    }

    try {
      const reportConfig = this.buildReportConfig();
      const reportData = {
        name: this.reportName,
        description: this.reportDescription,
        dataSource: reportConfig.dataSource,
        fields: reportConfig.fields,
        filters: reportConfig.filters,
        dateRangeFilter: reportConfig.dateRangeFilter,
        chartConfig: reportConfig.chartConfig,
        isPublic: this.isPublic
      };

      await this.customReportService.saveSimpleReport(reportData);
      
      this.customMessageService.success('Success', 'Report saved successfully');

      this.showSaveDialog = false;
      await this.loadSavedReports();
    } catch (error: any) {
      this.customMessageService.error('Error', error.message || 'Failed to save report');
    }
  }

  openLoadDialog() {
    this.showLoadDialog = true;
    this.loadSavedReports();
  }

  async loadReport(report: any) {
    try {
      console.log('Loading report:', report); // Debug log
      
      // Validate report data
      if (!report || !report.dataSource || !report.fields) {
        this.customMessageService.error('Invalid Report', 'Report data is incomplete or corrupted');
        return;
      }
      
      // Ensure field definitions are available
      if (!this.fieldDefinitions[report.dataSource]) {
        this.customMessageService.error('Data Source Error', `Field definitions not found for data source: ${report.dataSource}`);
        return;
      }
      
      // Load the report configuration
      this.selectedDataSource = report.dataSource;
      
      // Convert field names to ReportField objects
      if (report.fields && Array.isArray(report.fields)) {
        this.selectedFields = report.fields.map((fieldName: string) => {
          // Find the field definition from available fields
          const availableFields = this.fieldDefinitions[report.dataSource] || [];
          const fieldDef = availableFields.find(f => f.name === fieldName);
          if (fieldDef) {
            return fieldDef;
          } else {
            // Create a fallback field definition
            console.warn(`Field definition not found for: ${fieldName}, creating fallback`);
            return { 
              name: fieldName, 
              label: fieldName, 
              type: 'string', 
              source: 'main', 
              path: fieldName, 
              category: 'main', 
              accessLevel: [],
              isVisible: true,
              isFilterable: true,
              isChartable: false
            };
          }
        }).filter((f: any) => f); // Remove any undefined fields
      } else {
        this.selectedFields = [];
      }
      
      this.selectedFilters = report.filters || [];
      this.chartConfig = report.chartConfig || { enabled: false };
      
      // Load date range filter if present
      if (report.dateRangeFilter) {
        this.dateRangeFilter = {
          enabled: true,
          field: report.dateRangeFilter.field || '',
          startDate: report.dateRangeFilter.startDate ? new Date(report.dateRangeFilter.startDate) : null,
          endDate: report.dateRangeFilter.endDate ? new Date(report.dateRangeFilter.endDate) : null
        };
      } else {
        this.dateRangeFilter = {
          enabled: false,
          field: '',
          startDate: null,
          endDate: null
        };
      }
      
      // Update form values - ensure all required fields are set
      this.reportForm.patchValue({
        name: report.name || '',
        description: report.description || '',
        dataSource: report.dataSource
      });

      console.log('Loaded fields:', this.selectedFields); // Debug log
      console.log('Selected data source:', this.selectedDataSource); // Debug log
      
      this.showLoadDialog = false;
      
      this.customMessageService.success('Report Loaded', `Report "${report.name}" loaded successfully`);

      // Execute the loaded report
      await this.executeReport();
      
    } catch (error: any) {
      console.error('Error loading report:', error); // Debug log
      this.customMessageService.error('Load Failed', error.message || 'Failed to load report');
    }
  }

  async deleteReport(reportId: string) {
    try {
      await this.customReportService.deleteSavedSimpleReport(reportId);
      
      this.customMessageService.success('Report Deleted', 'Report deleted successfully');

      await this.loadSavedReports();
    } catch (error: any) {
      console.error('Delete report error:', error);
      this.customMessageService.error('Delete Failed', error.message || 'Failed to delete report');
    }
  }

  cancelSave() {
    this.showSaveDialog = false;
    this.reportName = '';
    this.reportDescription = '';
    this.isPublic = false;
  }

  cancelLoad() {
    this.showLoadDialog = false;
  }

  // ============================================
  // Static Reports Methods
  // ============================================

  switchToTab(tab: 'dynamic' | 'static') {
    this.activeTab = tab;
    this.selectedStaticReport = null;
    this.staticReportData = null;
    this.customMessageService.clearAll();
  }

  hasAccessToReport(allowedRoles: string[]): boolean {
    // Get current user role
    if (!this.currentUser || !this.currentUser.roleId) {
      console.warn('Current user or roleId not available, allowing access for debugging');
      return true; // Allow access if user context not loaded yet
    }
    
    const userRole = this.currentUser.roleId.identification || 'USER';
    const hasAccess = allowedRoles.includes(userRole);
    console.log('Checking access:', { userRole, allowedRoles, hasAccess });
    return hasAccess;
  }

  async selectStaticReport(report: any) {
    if (!this.hasAccessToReport(report.allowedRoles)) {
      this.customMessageService.error('Access Denied', 'You do not have permission to access this report');
      return;
    }

    this.selectedStaticReport = report.id;
    this.customMessageService.clearAll();
    
    await this.loadStaticReportData(report.id);
  }

  async loadStaticReportData(reportId: string) {
    this.loading = true;
    
    try {
      switch (reportId) {
        case 'leaderboard':
          const leaderboardResult = await this.staticReportService.getLeaderboard(
            this.selectedMonth,
            this.selectedYear,
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          this.staticReportData = leaderboardResult.data;
          this.totalRecordsStatic = leaderboardResult.total;
          break;
          
        case 'product':
          const productResult = await this.staticReportService.getProductReport(
            this.selectedSubscriberId || undefined
          );
          this.staticReportData = productResult;
          break;
          
        case 'team-activity':
          const teamActivityResult = await this.staticReportService.getTeamActivity(
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          this.staticReportData = teamActivityResult.data;
          this.totalRecordsStatic = teamActivityResult.total;
          break;
          
        case 'pipeline-alert':
          const pipelineResult = await this.staticReportService.getPipelineAlert(
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          this.staticReportData = pipelineResult.data;
          this.totalRecordsStatic = pipelineResult.total;
          break;
          
        case 'fsr-performance':
          const fsrPerfResult = await this.staticReportService.getFSRTeamPerformance(
            undefined,
            undefined,
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          this.staticReportData = fsrPerfResult.data;
          this.totalRecordsStatic = fsrPerfResult.total;
          break;
          
        case 'personal-pipeline':
          const pipelineTypeResult = await this.staticReportService.getPersonalPipeline(
            this.personalPipelineType,
            this.currentPageStatic,
            this.pageSizeStatic
          );
          this.staticReportData = pipelineTypeResult.data;
          this.totalRecordsStatic = pipelineTypeResult.total;
          break;
          
        case 'monthly-winloss':
          const winLossResult = await this.staticReportService.getMonthlyWinLoss(
            this.selectedMonth,
            this.selectedYear,
            this.monthlyWinLossType,
            this.currentPageStatic,
            this.pageSizeStatic
          );
          this.staticReportData = winLossResult.data;
          this.totalRecordsStatic = winLossResult.total;
          break;
          
        case 'personal-performance':
          const personalPerfResult = await this.staticReportService.getPersonalPerformance();
          this.staticReportData = personalPerfResult;
          break;
      }
      
    } catch (error: any) {
      console.error('Error loading static report:', error);
      this.customMessageService.error('Error', error.message || 'Failed to load report data');
    } finally {
      this.loading = false;
    }
  }

  async onPageChangeStatic(event: any) {
    this.currentPageStatic = event.page + 1;
    this.pageSizeStatic = event.rows;
    
    if (this.selectedStaticReport) {
      await this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  async refreshStaticReport() {
    if (this.selectedStaticReport) {
      await this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  async exportStaticReport(format: 'csv' | 'excel' | 'pdf') {
    if (!this.selectedStaticReport || !this.staticReportData) {
      this.customMessageService.warning('No Data', 'No data available to export');
      return;
    }

    try {
      this.loading = true;
      
      const exportObservable = this.staticReportService.exportReport(
        this.selectedStaticReport,
        format,
        this.getExportParams()
      );

      const blob = await new Promise<Blob>((resolve, reject) => {
        exportObservable.subscribe({
          next: (blob) => resolve(blob),
          error: (err) => reject(err)
        });
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.selectedStaticReport}-${new Date().toISOString().split('T')[0]}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);

      this.customMessageService.success('Export Completed', `Report exported to ${format.toUpperCase()} successfully`);
      
    } catch (error: any) {
      console.error('Export error:', error);
      this.customMessageService.error('Export Failed', error.message || 'Failed to export report');
    } finally {
      this.loading = false;
    }
  }

  getExportParams(): any {
    const params: any = {};
    
    if (this.selectedStaticReport === 'leaderboard' || this.selectedStaticReport === 'monthly-winloss') {
      params.month = this.selectedMonth;
      params.year = this.selectedYear;
    }
    
    if (this.selectedStaticReport === 'personal-pipeline') {
      params.type = this.personalPipelineType;
    }
    
    if (this.selectedStaticReport === 'monthly-winloss') {
      params.type = this.monthlyWinLossType;
    }
    
    return params;
  }

  getMonthName(month: number): string {
    return new Date(2024, month - 1).toLocaleString('en-US', { month: 'long' });
  }

  togglePersonalPipelineType() {
    this.personalPipelineType = this.personalPipelineType === 'active' ? 'expired' : 'active';
    if (this.selectedStaticReport === 'personal-pipeline') {
      this.loadStaticReportData('personal-pipeline');
    }
  }

  toggleMonthlyWinLossType() {
    this.monthlyWinLossType = this.monthlyWinLossType === 'wins' ? 'losses' : 'wins';
    if (this.selectedStaticReport === 'monthly-winloss') {
      this.loadStaticReportData('monthly-winloss');
    }
  }

  onChangeMonth() {
    if (this.selectedStaticReport === 'leaderboard' || this.selectedStaticReport === 'monthly-winloss') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  onChangeYear() {
    if (this.selectedStaticReport === 'leaderboard' || this.selectedStaticReport === 'monthly-winloss') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  goBackToReports() {
    this.selectedStaticReport = null;
    this.staticReportData = null;
  }

  getAvailableStaticReports() {
    const available = this.staticReports.filter(report => this.hasAccessToReport(report.allowedRoles));
    console.log('Available static reports:', available.length, 'out of', this.staticReports.length);
    return available;
  }

  getStaticReportName(reportId: string | null): string {
    if (!reportId) return '';
    const report = this.staticReports.find(r => r.id === reportId);
    return report?.name || '';
  }

  getStaticReportFrequency(reportId: string | null): string {
    if (!reportId) return '';
    const report = this.staticReports.find(r => r.id === reportId);
    return report?.frequency || '';
  }

  private initializeStaticExportMenuItems() {
    this.exportMenuItemsStatic = [
      {
        label: 'Export CSV',
        icon: 'pi pi-file',
        command: () => this.exportStaticReport('csv')
      },
      {
        label: 'Export Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportStaticReport('excel')
      },
      {
        label: 'Export PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.exportStaticReport('pdf')
      }
    ];
  }

  exportMenuItemsStatic: any[] = [];
}
